// stub out cheerio using native dom methods for frontend so we don't have to bundle cheerio on the frontend
export function load (html) {
  const doc = parseTeddyDOMFromString(html) // create a DOM

  // return a querySelector function with function chains
  //
  // e.g. dom('include') or dom(el) from teddy
  const $ = function (query) { // query can be a string, or a dom object
    // if query is a string, we need to create a dom object from the string: an object with elements in it, e.g. a list of include tag objects
    if (typeof query === 'string') {
      return queryAll(doc, query) // return the object collection
    }

    // if query is an object, it's assumed we're trying to perform operations on a single dom node
    const el = query
    return {

      // e.g. dom(el).children() from teddy
      children: function () {
        return Array.from(childrenOf(el).childNodes).filter(node => node.nodeType === 1)
      },

      // e.g. dom(el).contents() from teddy, which is every child node rather than only the elements among them
      contents: function () {
        return childrenOf(el).childNodes
      },

      // e.g. dom(el).find() from teddy
      find: function (selector) {
        return queryAll(el, selector)
      },

      // e.g. dom(arg).html() from teddy
      html: function () {
        return getTeddyDOMInnerHTML(el)
      },

      // e.g. dom(arg).toString() from teddy
      toString: function () {
        return getTeddyDOMOuterHTML(el)
      },

      // e.g. dom(el).attr('teddydeferreddynamicinclude', 'true') from teddy
      attr: function (attr, val) {
        return el.setAttribute(attr, val)
      },

      // dom(el).removeAttr(attr) from teddy
      removeAttr: function (attr) {
        return el.removeAttribute(attr)
      },

      // e.g. dom(el).replaceWith(localDom.html()) from teddy
      replaceWith: function (html) {
        // can either be a string or an array of elements
        if (typeof html === 'object') {
          let newHtml = ''
          for (const el of html) {
            if (el.nodeType === window.Node.COMMENT_NODE) newHtml += '<!--' + el.textContent + '-->'
            else newHtml += el.outerHTML || el.textContent
          }
          html = newHtml
        }
        const temp = document.createElement('div')
        temp.innerHTML = html
        el.replaceWith(...temp.childNodes)
      },

      // e.g. dom(el).remove() from teddy
      remove: function () {
        return el.remove()
      }
    }
  }

  // e.g. dom.html() from teddy
  $.html = function () {
    return getTeddyDOMInnerHTML(doc)
  }

  // e.g. dom.toString() from teddy
  $.toString = function () {
    return getTeddyDOMOuterHTML(doc)
  }

  return $
}

load.isCheerioPolyfill = true

// DOM parser function like DOMParser's parseFromString but allows Teddy elements to exist in places where they otherwise wouldn't be allowed, like inside of <select> elements
function parseTeddyDOMFromString (html) {
  const selfClosingTags = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'])
  const root = document.createElement('body')
  const dom = [root]
  const openTags = [] // stack to track open tags
  const tagAndCommentRegex = /<\/?([a-zA-Z][a-zA-Z0-9-]*)([^>]*)>|<!--([\s\S]*?)-->/g
  // the = belongs to both forms of a value. it used to sit inside the quoted form only, so an unquoted value such as src=x was read as the value "=x"
  const attrRegex = /([a-zA-Z0-9-:._]+)(?:=(?:(["'])(.*?)\2|([^>\s]+)))?/g
  // elements whose contents a browser reads as plain text rather than as markup, so a tag written inside one is not a tag. teddy leaves their contents alone on the server for the same reason
  const rawTextTags = new Set(['script', 'style', 'textarea'])
  let lastIndex = 0
  let match

  // loop through each match and build a DOM
  while ((match = tagAndCommentRegex.exec(html)) !== null) {
    if (!dom[dom.length - 1]) throw new Error('Error parsing your template. There may be a coding mistake in your HTML. Look for extra closing </tags> and other common mistakes.')
    const textBeforeMatch = html.slice(lastIndex, match.index)

    // append text nodes
    if (textBeforeMatch.trim()) {
      const textNode = document.createTextNode(textBeforeMatch)
      childrenOf(dom[dom.length - 1]).appendChild(textNode)
    }

    if (match[0].startsWith('<!--')) {
      // handle comments
      const commentNode = document.createComment(match[3])
      childrenOf(dom[dom.length - 1]).appendChild(commentNode)
    } else {
      // handle tags
      const [fullMatch, tagName, attrString] = match
      const lowerCaseTagName = tagName.toLowerCase()
      const isClosingTag = fullMatch.startsWith('</')
      if (isClosingTag) {
        if (selfClosingTags.has(lowerCaseTagName)) {
          // convert incorrect closing tag for self-closing tag to self-closing tag
          const element = document.createElement(tagName)
          childrenOf(dom[dom.length - 1]).appendChild(element)
        } else {
          // check if the closing tag matches the most recent open tag
          if (openTags.length > 0 && openTags[openTags.length - 1] === lowerCaseTagName) {
            openTags.pop()
            dom.pop()
          }
        }
      } else {
        // create a new element
        const element = document.createElement(tagName)

        // set attributes
        let attrMatch
        const attrMap = new Map()
        while ((attrMatch = attrRegex.exec(attrString)) !== null) {
          const attrName = attrMatch[1]
          const attrValue = attrMatch[3] || attrMatch[4] || ''

          // handle duplicate attributes for special tags
          if (attrMap.has(attrName)) {
            let count = 1
            let newAttrName
            do {
              newAttrName = `${attrName}-teddyduplicate${count}`
              count++
            } while (attrMap.has(newAttrName))
            attrMap.set(newAttrName, attrValue)
          } else attrMap.set(attrName, attrValue)
        }

        // apply attributes to the element
        for (const [name, value] of attrMap) {
          try {
            // replace elements with `src` attributes with `data-teddy-defer-attr-src` so the browser doesn't try to prefetch the asset
            //
            // this is needed because the value of the `src` attribute could be a {teddyVariable} and that fetch won't resolve
            switch (lowerCaseTagName) {
              case 'img':
              case 'video':
              case 'audio':
              case 'iframe':
              case 'script':
                if (name === 'src') element.setAttribute('data-teddy-defer-attr-src', value) // replace src with data-teddy-defer-attr-src
                else element.setAttribute(name, value || '')
                break
              case 'link':
                if (name === 'href') element.setAttribute('data-teddy-defer-attr-href', value) // replace src with data-teddy-defer-attr-href
                else element.setAttribute(name, value || '')
                break
              default:
                element.setAttribute(name, value || '')
            }
          } catch (e) {
            console.warn('Error parsing an element attribute. You might have a typo in your HTML. A common cause is two spaces between element attributes.')
          }
        }

        // a template's children go into a fragment of its own, which has no way back to the template it belongs to. teddy walks up from an element to see what encloses it, so the way back is written here: without it a construct inside a template would look like the outermost one
        if (isTemplate(element)) element.content.teddyTemplateHost = element

        // append the new element to the current parent
        childrenOf(dom[dom.length - 1]).appendChild(element)

        // push the new element to the dom if it's not self-closing
        if (!selfClosingTags.has(lowerCaseTagName) && !fullMatch.endsWith('/>')) {
          if (rawTextTags.has(lowerCaseTagName)) {
            // everything up to the element's closing tag is its text, however much of it looks like markup
            const closeTag = new RegExp(`</${lowerCaseTagName}\\s*>`, 'ig')
            closeTag.lastIndex = tagAndCommentRegex.lastIndex
            const close = closeTag.exec(html)
            const end = close ? close.index : html.length
            const text = html.slice(tagAndCommentRegex.lastIndex, end)
            if (text) element.appendChild(document.createTextNode(text))
            tagAndCommentRegex.lastIndex = close ? closeTag.lastIndex : html.length
          } else {
            dom.push(element)
            openTags.push(lowerCaseTagName)
          }
        }
      }
    }

    lastIndex = tagAndCommentRegex.lastIndex
  }

  // append any remaining text after the last match
  if (lastIndex < html.length) {
    const remainingText = html.slice(lastIndex)
    if (remainingText.trim()) {
      const textNode = document.createTextNode(remainingText)
      childrenOf(dom[dom.length - 1]).appendChild(textNode)
    }
  }

  return root
}

// custom functions to get inner/outer HTML without escaping various things to prevent teddy from infinitely escaping them
const doublyEncodedEntities = {
  '&amp;amp;': '&amp;',
  '&amp;lt;': '&lt;',
  '&amp;gt;': '&gt;',
  '&amp;quot;': '&quot;',
  '&amp;#39;': '&#39;',
  '&amp;#x2F;': '&#x2F;'
}
// built once rather than on every node of every serialization
const entityFixes = Object.entries(doublyEncodedEntities).map(([doublyEncoded, singleEncoded]) => [new RegExp(doublyEncoded, 'g'), singleEncoded])

// teddy renames src and href while the markup is a live dom, so that the browser does not try to fetch a url that is still a {variable}. a string is not a dom and cannot fetch anything, so the real attribute name goes back on as the markup leaves the dom. doing it here rather than at the end of a render is what saves every render a sweep of its whole output looking for these
const deferredAttributes = /data-teddy-defer-attr-(src|href)/g

// undoes what parsing the markup into a dom did to it: the double encoding a browser's serializer applies, and the attribute names teddy renamed on the way in
function undoParserArtifacts (html) {
  for (const [pattern, singleEncoded] of entityFixes) html = html.replace(pattern, singleEncoded)
  return html.replace(deferredAttributes, '$1')
}
// every element under a root that matches a selector, including the ones inside a template
//
// a query never reaches into a template's content fragment, so a teddy tag written inside a template went unclaimed in a browser while node claimed it. asking for the templates in the same query keeps everything in the order it was written in
function queryAll (root, selector) {
  const found = []
  for (const el of root.querySelectorAll(`${selector}, template`)) {
    if (el.matches(selector)) found.push(el)
    if (isTemplate(el)) found.push(...queryAll(el.content, selector))
  }
  return found
}

// where the children of a node belong
//
// a template element keeps its children in a document fragment of its own rather than among its child nodes, and serializing a template writes that fragment out. so a child appended to the element itself is kept somewhere the markup never shows it, which is how everything inside a <template> used to disappear from a browser render
function childrenOf (node) {
  return isTemplate(node) ? node.content : node
}

// a template element, and not merely something with a content property: <meta content="..."> has one of those and it is a string
function isTemplate (node) {
  return node.nodeName === 'TEMPLATE' && node.content
}

function getTeddyDOMInnerHTML (node) {
  // build html string
  let html = ''
  for (const child of childrenOf(node).childNodes) {
    if (child.nodeType === window.Node.ELEMENT_NODE) {
      html += undoParserArtifacts(child.outerHTML)
    } else if (child.nodeType === window.Node.TEXT_NODE) {
      html += undoParserArtifacts(child.textContent)
    } else if (child.nodeType === window.Node.COMMENT_NODE) {
      html += `<!--${undoParserArtifacts(child.textContent)}-->`
    }
  }

  return html
}
function getTeddyDOMOuterHTML (node) {
  // start with the outerHTML of the node
  let outerHTML = ''

  if (node.nodeType === window.Node.ELEMENT_NODE) {
    outerHTML = node.outerHTML
  } else if (node.nodeType === window.Node.TEXT_NODE) {
    outerHTML = node.textContent
  } else if (node.nodeType === window.Node.COMMENT_NODE) {
    outerHTML = `<!--${node.textContent}-->`
  }

  return undoParserArtifacts(outerHTML)
}
